import { Box, Typography, Chip } from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import BugReportIcon from "@mui/icons-material/BugReport";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutlined";

const ENTRY_TYPES = {
  feature:     { label: "New",         icon: AutoFixHighIcon,         bg: "#eff6ff", text: "#1d4ed8", darkBg: "#1e3a5f", darkText: "#93c5fd" },
  fix:         { label: "Fix",          icon: BugReportIcon,           bg: "#fef3c7", text: "#92400e", darkBg: "#431407", darkText: "#fcd34d" },
  improvement: { label: "Improvement", icon: TrendingUpIcon,          bg: "#f0fdf4", text: "#166534", darkBg: "#14532d", darkText: "#86efac" },
  removed:     { label: "Removed",     icon: RemoveCircleOutlineIcon, bg: "#fef2f2", text: "#991b1b", darkBg: "#450a0a", darkText: "#fca5a5" },
};

const CHANGELOG = [
  {
    date: "2026-07-28",
    entries: [
      { type: "removed", text: "Removed the World Cup 2026 page from the app navigation following the conclusion of the tournament." },
    ],
  },
  {
    date: "2026-07-11",
    entries: [
      { type: "fix",         text: "Fixed an issue where the World Cup schedule page would scroll to the top of the list instead of the next upcoming match day when there are no games scheduled today." },
      { type: "improvement", text: "Improved the GO Schedule page by removing the 5-minute departure threshold so trains are shown as upcoming right up until they depart." },
      { type: "feature",     text: "Added support for tapping the app logo in the header to navigate to the user's default page." },
    ],
  },
  {
    date: "2026-06-23",
    entries: [
      { type: "feature", text: "Added support for a World Cup 2026 page showing the full tournament schedule with live scores, team flags, kickoff times, and venue information, powered by the ESPN API." },
      { type: "feature", text: "Added support for a Settings page where visitors can set a default landing page, preferred color mode, and default Police & Fire division — preferences are saved to the device and applied automatically on return visits." },
    ],
  },
  {
    date: "2026-06-04",
    entries: [
      { type: "feature", text: "Added support for a 'Get Home from Union' widget on the GO Schedule page that compares estimated travel time via GO Train to Danforth GO and TTC Line 2 to Main Street, including live GO departure times and active Line 2 alerts." },
    ],
  },
  {
    date: "2026-05-27",
    entries: [
      { type: "feature", text: "Added support for a Weather & Pollen page showing current conditions, pollen levels, air quality, sunrise/sunset, health indices, and severe weather alerts for Toronto." },
      { type: "fix",     text: "Fixed an issue where Transit Status map segments were not highlighting correctly when a Line 1 service disruption was active." },
      { type: "fix",     text: "Fixed an issue where stale speed restriction alerts more than 7 days old continued to appear in the Transit Status list." },
      { type: "fix",     text: "Fixed an issue where Glencairn, Cedarvale, Lawrence West, and several other Line 1 stations were positioned incorrectly on the Transit Status map." },
      { type: "fix",     text: "Fixed an issue where the Transit Status page header, footer, and icons appeared smaller than the rest of the app." },
      { type: "feature", text: "Added support for external links to TTC, GO Transit, and UP Express websites in the Transit Status section headers." },
    ],
  },
];

function fmtDate(dateStr) {
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-CA", {
    year: "numeric", month: "long", day: "numeric",
  });
}

export default function ChangelogPage({ dark, border, barBg }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* Header */}
      <Box sx={{ height: 52, display: "flex", alignItems: "center", px: 2,
        borderBottom: `1px solid ${border}`, bgcolor: barBg, flexShrink: 0 }}>
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>Changelog</Typography>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", bgcolor: "background.default" }}>
        <Box sx={{ px: 2, py: 2, maxWidth: 640, display: "flex", flexDirection: "column", gap: 3 }}>

          {CHANGELOG.map(({ date, entries }) => (
            <Box key={date}>

              {/* Date heading */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "text.secondary",
                  textTransform: "uppercase", letterSpacing: "0.07em", whiteSpace: "nowrap" }}>
                  {fmtDate(date)}
                </Typography>
                <Box sx={{ flex: 1, height: "1px", bgcolor: border }} />
              </Box>

              {/* Entries */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {entries.map((entry, i) => {
                  const t = ENTRY_TYPES[entry.type];
                  const Icon = t.icon;
                  return (
                    <Box key={i} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start",
                      bgcolor: "background.paper", border: `1px solid ${border}`,
                      borderRadius: 2, px: 1.5, py: 1.25 }}>
                      <Box sx={{ mt: 0.2, flexShrink: 0 }}>
                        <Icon sx={{ fontSize: 16,
                          color: dark ? t.darkText : t.text }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Chip label={t.label} size="small" sx={{
                          fontSize: "0.65rem", height: 18, mb: 0.5, fontWeight: 700,
                          bgcolor: dark ? t.darkBg : t.bg,
                          color: dark ? t.darkText : t.text,
                        }} />
                        <Typography sx={{ fontSize: "0.875rem", color: "text.primary", lineHeight: 1.5 }}>
                          {entry.text}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}

        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ height: 52, bgcolor: barBg, borderTop: `1px solid ${border}`, px: 2,
        display: "flex", alignItems: "center", flexShrink: 0 }}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
          MAIN // DANFORTH
        </Typography>
      </Box>
    </Box>
  );
}
